import { Toaster } from '@goaltree/ui/components/sonner'
import { TooltipProvider } from '@goaltree/ui/components/tooltip'
import { HeadContent, Outlet, createRootRouteWithContext } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'

import { CoachCallHost } from '@/components/coach-call-host'
import { CoachingAccent } from '@/components/coaching/coaching-mode'
import { SearchModal } from '@/components/search-modal'
import { ThemeProvider } from '@/components/theme-provider'
import { TitleTips } from '@/components/title-tips'
import { UsageLimitPrompt } from '@/components/usage-limit-prompt'
import { useAccentTheme } from '@/hooks/use-accent-theme'

import '../index.css'

// Phones: above the tab bar and the floating "New task" button (bottom 4.5rem + 3rem tall).
// No side insets: Sonner's phone stack is full width and each pill centres itself in it.
const MOBILE_TOAST_OFFSET = {
  bottom: 'calc(env(safe-area-inset-bottom, 0px) + 8.25rem)',
  left: '0px',
  right: '0px',
}

export interface RouterAppContext {}

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://goaltree.ai'

export const Route = createRootRouteWithContext<RouterAppContext>()({
  component: RootComponent,
  head: () => ({
    meta: [
      { title: 'Goaltree' },
      { name: 'description', content: 'Turn your goals into actionable, AI-generated plans.' },
      { property: 'og:site_name', content: 'Goaltree' },
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: 'Goaltree' },
      {
        property: 'og:description',
        content: 'Turn your goals into actionable, AI-generated plans.',
      },
      { property: 'og:image', content: `${SITE_URL}/og-image.png?v=4` },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Goaltree' },
      {
        name: 'twitter:description',
        content: 'Turn your goals into actionable, AI-generated plans.',
      },
      { name: 'twitter:image', content: `${SITE_URL}/og-image.png?v=4` },
    ],
    links: [{ rel: 'manifest', href: '/site.webmanifest' }],
  }),
})

function RootComponent() {
  useAccentTheme()

  return (
    <>
      <HeadContent />
      <ThemeProvider
        attribute="class"
        defaultTheme="dark"
        disableTransitionOnChange
        storageKey="vite-ui-theme"
      >
        <TooltipProvider>
          <Outlet />
        </TooltipProvider>
        <SearchModal />
        <UsageLimitPrompt />
        <TitleTips />
        <CoachingAccent />
        <CoachCallHost />
        {/* On phones the bottom is the tab bar, FAB, composers and the capture mic. */}
        {/* Expanded: an undo toast and a follow-up (e.g. the next stacked habit) must both stay readable. */}
        <Toaster position="bottom-center" mobileOffset={MOBILE_TOAST_OFFSET} expand />
      </ThemeProvider>
      <TanStackRouterDevtools position="bottom-left" />
    </>
  )
}
